import { Stack } from '@landler/component-library';
import { FC, HTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

import {
  PlotReportFactLabel,
  PlotReportFactLabelNameEnum,
  PopulationDensityBenchmark,
  ValueClass,
} from '@/api/rest/resources/types/fact';
import { INDICATOR } from '@/api/rest/resources/types/IndicatorProduct';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { ChartProps } from '@/components/Charts/types';
import { getChartDataFromGraphFact, getDataDefaultDomain, sortChartData } from '@/components/Charts/utils';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarkForFact } from '@/pages/shared/hooks/useBenchmarkForFact';
import { isNullish } from '@/utils/booleans';
import { printYear } from '@/utils/formatting/date';
import { getColorForIndicator } from '@/utils/getColorForIndicator';

import { BenchmarkPill } from '../Pill/BenchmarkPill';
import { ValueClassPill } from '../Pill/ValueClassPill';
import { Chart, ComposedChart, getXAxisProps, Line, TooltipProps } from './components/Chart';
import { CHART_ZOOM } from './components/ChartZoom';
import { ChartLegendBar } from './components/LegendBar';
import { ChartTooltip, ChartTooltipTextPrimary, ChartTooltipTextSecondary } from './components/Tooltip';
import { ChartLegendBarItem } from './components/types';
import { useYAxisDomainFromMouseWheelZoom } from './hooks/useYAxisDomainFromMouseWheelZoom';
import { NoChartData } from './NoChartData';
import { extrapolateReferenceLinesChartData, RenderReferenceLine } from './ReferenceLines';
import { getChartZoomedDomain } from './utils/getChartZoomedDomain';

const populationDensityColors = getColorForIndicator(INDICATOR.POPULATION_DENSITY);

const CHART_COLORS = {
  populationDensity: {
    bar: {
      fill: populationDensityColors.fill,
    },
  },
} as const;

const UNIT = UnitEnum['people/km^2'];
const POPULATION_DENSITY_LABEL = 'shared.ncaDetail.details.populationDensity.labels.populationDensity';

const factNames = {
  population_density: 'population_density',
  population_density_benchmark_urban_center: 'population_density_benchmark_urban_center',
  population_density_benchmark_urban_cluster: 'population_density_benchmark_urban_cluster',
  population_density_benchmark_rural: 'population_density_benchmark_rural',
};

export const PopulationDensityHistoricalChart: FC<HTMLAttributes<HTMLDivElement> & ChartProps> = ({
  data,
  zoom,
  setZoom,
  benchmarks,
  ...delegated
}) => {
  const { t } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const defaultDomain = getDataDefaultDomain(data, 0.8, 1.2);
  const yAxisDomainFromMouseWheel = useYAxisDomainFromMouseWheelZoom(chartContainerRef, defaultDomain, setZoom);
  const yAxisDomainFromButtons = getChartZoomedDomain({ data, zoom });

  const dateDataMap = getChartDataFromGraphFact(data, factNames);
  const sortedChartData = sortChartData(dateDataMap, (date) => new Date(date).getTime());

  const firstTick = sortedChartData.at(0);
  const lastTick = sortedChartData.at(-1);

  if (!firstTick || !lastTick) {
    Logger.error('Not enough data to render chart');
    return (
      <div className='relative flex h-full w-full flex-col justify-center' {...delegated}>
        <NoChartData />
      </div>
    );
  }

  const xTicksDomain = [new Date(firstTick.label).getFullYear(), new Date(lastTick.label).getFullYear()];

  const xAxisProps = {
    ...getXAxisProps(xTicksDomain),
    ticks: Array.from(new Set(sortedChartData.map((dataPoint) => dataPoint.label as number))), // The population density data is in decades, to prevent having ticks for every year with no data, we set the ticks to be only the years present in the data
    tickFormatter: printYear,
  };

  let chartData = extrapolateReferenceLinesChartData(
    xTicksDomain,
    sortedChartData,
    factNames.population_density_benchmark_urban_center,
  );
  chartData = extrapolateReferenceLinesChartData(
    xTicksDomain,
    chartData,
    factNames.population_density_benchmark_urban_cluster,
  );
  chartData = extrapolateReferenceLinesChartData(xTicksDomain, chartData, factNames.population_density_benchmark_rural);

  const yAxisDomain = zoom === CHART_ZOOM.MOUSE_WHEEL ? yAxisDomainFromMouseWheel : yAxisDomainFromButtons;

  return (
    <>
      <Chart.Container
        ref={chartContainerRef}
        {...delegated}
        data={chartData}
        config={{
          [factNames.population_density]: {
            label: t(POPULATION_DENSITY_LABEL),
            color: CHART_COLORS.populationDensity.bar.fill,
          },
        }}
        data-testid='population-density-chart'
      >
        <ComposedChart data={chartData}>
          <Line
            dataKey={`${[factNames.population_density]}.value`}
            stroke={CHART_COLORS.populationDensity.bar.fill}
            className='population-density-line'
            strokeWidth={4}
            dot={false}
          />
          {RenderReferenceLine({
            dataKey: `${factNames.population_density_benchmark_urban_center}.value`,
            type: 'monotone',
            label: chartData.some((point) => !!point[factNames.population_density_benchmark_urban_center])
              ? t('global.benchmark.urban_center')
              : null,
            chartData,
            id: 'benchmark-urban-center',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: `${factNames.population_density_benchmark_urban_cluster}.value`,
            type: 'monotone',
            label: chartData.some((point) => !!point[factNames.population_density_benchmark_urban_cluster])
              ? t('global.benchmark.urban_cluster')
              : null,
            chartData,
            id: 'benchmark-urban-cluster',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          {RenderReferenceLine({
            dataKey: `${factNames.population_density_benchmark_rural}.value`,
            type: 'monotone',
            label: chartData.some((point) => !!point[factNames.population_density_benchmark_rural])
              ? t('global.benchmark.rural')
              : null,
            chartData,
            id: 'benchmark-rural',
            yAxisDomain: yAxisDomain as [number, number],
          })}
          <Chart.XAxis {...xAxisProps} className='population-density-x-axis' />
          <Chart.YAxis domain={yAxisDomain} allowDataOverflow={true} className='population-density-y-axis'>
            <Chart.Label>{t(`global.unit.${UNIT}`, UNIT)}</Chart.Label>
          </Chart.YAxis>
          <Chart.Tooltip content={(props) => <CustomTooltip benchmarks={benchmarks} {...props} />} />
        </ComposedChart>
      </Chart.Container>
      <CustomLegendBar />
    </>
  );
};

const CustomLegendBar = () => {
  const { t } = useTranslation();

  const legendBarItems: ChartLegendBarItem[] = [
    {
      label: t(POPULATION_DENSITY_LABEL),
      color: CHART_COLORS.populationDensity.bar.fill,
      shape: 'line',
    },
  ];

  return (
    <ChartLegendBar items={legendBarItems} className='sm:ml-[28px]' data-testid='population-density-chart-legend' />
  );
};

const CustomTooltip = ({
  active,
  payload,
  label,
  benchmarks,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: TooltipProps<ValueType, NameType> & { benchmarks: any }) => {
  const { t } = useTranslation();

  const pdValue = payload?.find((data) => data.dataKey === `${[factNames.population_density]}.value`);
  const benchmarkType = useBenchmarkForFact<PopulationDensityBenchmark>(benchmarks, pdValue?.value, [
    PopulationDensityBenchmark.rural,
    PopulationDensityBenchmark.urban_cluster,
    PopulationDensityBenchmark.urban_center,
  ]);

  if (!active) return null;

  const unitDisplay = t(`global.unit.${UNIT}`, UNIT);
  const pdDisplay = !isNullish(pdValue?.value)
    ? `${getDisplayNumber(pdValue?.value as string, window.navigator.language)} ${unitDisplay}`
    : t('global.analysis.noData');

  const dataPointHasLabel = pdValue && pdValue?.payload?.[factNames.population_density]?.labels?.length > 0;
  const dataPointValueClass = dataPointHasLabel
    ? pdValue.payload[factNames.population_density].labels.find(
        (dataLabel: PlotReportFactLabel) => dataLabel.name === PlotReportFactLabelNameEnum.value_class,
      )
    : undefined;
  const dataLabel = dataPointValueClass?.value as ValueClass;

  return (
    <ChartTooltip>
      <Stack spacing={8} direction='row' className='justify-between'>
        <ChartTooltipTextSecondary>{printYear(label)}</ChartTooltipTextSecondary>
        {dataLabel && <ValueClassPill title={dataLabel} />}
      </Stack>
      {benchmarkType && (
        <div className='typography-body2 text-text-secondary'>
          <BenchmarkPill benchmarkType={benchmarkType} />
        </div>
      )}
      <Stack spacing={8} direction='row'>
        <ChartTooltipTextPrimary>{t(POPULATION_DENSITY_LABEL)}</ChartTooltipTextPrimary>
        <ChartTooltipTextPrimary>{pdDisplay}</ChartTooltipTextPrimary>
      </Stack>
    </ChartTooltip>
  );
};

export { CustomTooltip as CustomTooltipPopulationDensity };
